import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 769;

class ContentImageSwiper extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._paginationHandlers = [];
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this._updateLayout();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    this._removePaginationHandlers();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      sectionId: this.dataset.sectionId || "",
      spaceBetween: parseInt(this.dataset.spaceBetween || "20", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.2"),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
      enableAutoplay: this.dataset.enableAutoplay === "true",
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _shouldUseSwiper(config) {
    if (config.slideCount <= 0) return false;
    if (this._isMobile()) return true;
    return config.slideCount > 1;
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "cis__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyStaticMode() {
    this.classList.add("cis--static");
    this.classList.remove("cis--swiper", "cis--fade");
    this._cleanupSwiperClasses();
    this._syncSlideAccessibility(0);
    this._syncPaginationState(0);
  }

  _prepareSwiperStructure() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "cis__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { slideEls };
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  _syncPaginationState(activeIndex) {
    const buttons = this.querySelectorAll(".cis__pagination-btn[data-slide-index]");
    buttons.forEach((button) => {
      const index = parseInt(button.dataset.slideIndex || "0", 10);
      const isActive = index === activeIndex;
      button.classList.toggle("cis__pagination-btn--active", isActive);
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) {
        button.setAttribute("aria-current", "true");
      } else {
        button.removeAttribute("aria-current");
      }
    });
  }

  _removePaginationHandlers() {
    this._paginationHandlers.forEach(({ button, handler }) => {
      button.removeEventListener("click", handler);
    });
    this._paginationHandlers = [];
  }

  _bindPagination(config) {
    this._removePaginationHandlers();
    if (!config.showPagination) return;

    const buttons = this.querySelectorAll(".cis__pagination-btn[data-slide-index]");
    buttons.forEach((button) => {
      const handler = () => {
        const index = parseInt(button.dataset.slideIndex || "0", 10);
        if (!this._swiper || this._swiper.destroyed) {
          this._syncSlideAccessibility(index);
          this._syncPaginationState(index);
          return;
        }

        if (this._swiper.params.loop) {
          this._swiper.slideToLoop(index);
        } else {
          this._swiper.slideTo(index);
        }
      };

      button.addEventListener("click", handler);
      this._paginationHandlers.push({ button, handler });
    });
  }

  _initSwiper(config) {
    const structure = this._prepareSwiperStructure();
    if (!structure) return;

    const { slideEls } = structure;
    const slidesPerView = 1;
    const canNavigate = slideEls.length > 1;
    const canLoop = config.enableLoop && canNavigate && slideEls.length > slidesPerView;

    this.classList.add("cis--swiper", "cis--fade");
    this.classList.remove("cis--static");

    const swiperConfig = {
      slidesPerView,
      spaceBetween: 0,
      watchOverflow: true,
      grabCursor: canNavigate,
      speed: 500,
      effect: "fade",
      fadeEffect: { crossFade: true },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
      },
    };

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.enableAutoplay && canNavigate) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    if (config.showArrows && canNavigate) {
      const nextEl = this.querySelector(`.cis__nav-next--${config.sectionId}`);
      const prevEl = this.querySelector(`.cis__nav-prev--${config.sectionId}`);
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        const activeIndex = swiper.realIndex ?? swiper.activeIndex;
        this._syncSlideAccessibility(activeIndex);
        this._syncPaginationState(activeIndex);
        this._bindChartSliderMobileLayout();
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        const activeIndex = swiper.realIndex ?? swiper.activeIndex;
        this._syncSlideAccessibility(activeIndex);
        this._syncPaginationState(activeIndex);
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        swiper.update();
        this._bindChartSliderMobileLayout();
      },
    };

    const swiperEl = this.querySelector("[data-swiper]");
    this._swiper = new Swiper(swiperEl, swiperConfig);
    this._bindPagination(config);
    requestAnimationFrame(() => {
      this._swiper?.update();
      this._bindChartSliderMobileLayout();
    });
  }

  _clearChartSliderMobileLayout() {
    const swiperEl = this.querySelector("[data-swiper]");
    const toolbar = this.querySelector(".cis__toolbar");

    swiperEl?.style.removeProperty("min-height");
    toolbar?.style.removeProperty("--chart-slider-toolbar-top");

    this.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
      slide.style.removeProperty("min-height");
      slide.querySelector(".cis__media")?.style.removeProperty("min-height");
      slide.querySelector(".cis__panel")?.style.removeProperty("min-height");
    });
  }

  _measureChartSliderMobileHeights() {
    const viewport = this.querySelector(".cis__viewport");
    const slides = this.querySelectorAll("[data-swiper-slide]");
    const containerWidth = viewport?.clientWidth || window.innerWidth;
    let maxMedia = 0;
    let maxPanel = 0;

    slides.forEach((slide) => {
      const slideInner = slide.querySelector(".cis__slide-inner");
      const panel = slide.querySelector(".cis__panel");

      if (panel && slideInner) {
        const clone = panel.cloneNode(true);
        clone.style.cssText =
          "position:absolute;visibility:hidden;pointer-events:none;width:100%;left:0;top:0;";
        slideInner.appendChild(clone);
        maxPanel = Math.max(maxPanel, clone.offsetHeight);
        clone.remove();
      }

      const img = slide.querySelector(".cis__image:not(.cis__image--placeholder)");
      if (img?.naturalWidth > 0) {
        maxMedia = Math.max(
          maxMedia,
          containerWidth * (img.naturalHeight / img.naturalWidth)
        );
      } else {
        const media = slide.querySelector(".cis__media");
        if (media) {
          maxMedia = Math.max(maxMedia, media.getBoundingClientRect().height);
        }
      }
    });

    const spacerHeight = 44;
    return {
      maxMedia: Math.ceil(maxMedia),
      maxPanel: Math.ceil(maxPanel),
      maxSlide: Math.ceil(maxMedia) + spacerHeight + Math.ceil(maxPanel),
    };
  }

  _applyChartSliderMobileLayout() {
    if (!this.classList.contains("chart-slider") || window.innerWidth >= 769) {
      this._clearChartSliderMobileLayout();
      return;
    }

    const swiperEl = this.querySelector("[data-swiper]");
    const toolbar = this.querySelector(".cis__toolbar");
    const slides = this.querySelectorAll("[data-swiper-slide]");
    if (!swiperEl || !slides.length) return;

    const { maxMedia, maxPanel, maxSlide } = this._measureChartSliderMobileHeights();
    if (!maxSlide) return;

    slides.forEach((slide) => {
      const media = slide.querySelector(".cis__media");
      const panel = slide.querySelector(".cis__panel");
      if (media && maxMedia) media.style.minHeight = `${maxMedia}px`;
      if (panel && maxPanel) panel.style.minHeight = `${maxPanel}px`;
      slide.style.minHeight = `${maxSlide}px`;
    });

    swiperEl.style.minHeight = `${maxSlide}px`;
    toolbar?.style.setProperty("--chart-slider-toolbar-top", `${maxMedia}px`);
  }

  _bindChartSliderMobileLayout() {
    if (!this.classList.contains("chart-slider")) return;

    const runLayout = () => {
      requestAnimationFrame(() => this._applyChartSliderMobileLayout());
    };

    runLayout();

    const images = this.querySelectorAll(".cis__image:not(.cis__image--placeholder)");
    images.forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", runLayout, { once: true });
    });
  }

  _destroySwiper() {
    this._removePaginationHandlers();
    this._clearChartSliderMobileLayout();
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _updateLayout() {
    const config = this._readConfig();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyStaticMode();
      this._bindPagination(config);
      this._bindChartSliderMobileLayout();
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const config = this._readConfig();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);

      if (shouldUseSwiper !== isSwiperActive) {
        this._updateLayout();
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        this._swiper.update();
      }

      this._bindChartSliderMobileLayout();
    }, 150);
  }
}

if (!customElements.get("content-image-swiper")) {
  customElements.define("content-image-swiper", ContentImageSwiper);
}
